import { generateCoordinates as generatePinCoordinates } from '../../../../../javascript/screenshotPinning';
import { visualViewportPolyfill } from '../visualViewport';
import { TagInfoType } from './types';

export const getBoundingClientRectWithoutPadding = (
  target: HTMLElement
): { width: number; height: number; left: number; top: number } => {
  const { width, height, top, left } = target.getBoundingClientRect();

  const targetElementComputedStyle = window.getComputedStyle(target);

  let paddingToSubtract = { x: 0, y: 0 };

  // getBoundingClientRect doesn't factor padding if box-sizing is border-box, so we manually get it:
  if (
    targetElementComputedStyle.getPropertyValue('box-sizing') === 'border-box'
  ) {
    paddingToSubtract.x =
      parseFloat(targetElementComputedStyle.getPropertyValue('padding-left')) +
      parseFloat(targetElementComputedStyle.getPropertyValue('padding-right'));

    paddingToSubtract.y =
      parseFloat(targetElementComputedStyle.getPropertyValue('padding-top')) +
      parseFloat(targetElementComputedStyle.getPropertyValue('padding-bottom'));
  }

  return {
    width: width - paddingToSubtract.x,
    height: height - paddingToSubtract.y,
    top: top,
    left: left
  };
};

const mouseOffsetElementInPercentage = (
  target: HTMLElement,
  mouseX: number,
  mouseY: number
): {
  x: number;
  y: number;
} => {
  const { top, left, width, height } = getBoundingClientRectWithoutPadding(
    target
  );

  const mouseXRelativeToBox = mouseX - left;
  const mouseYRelativeToBox = mouseY - top;

  return {
    x: mouseXRelativeToBox / width,
    y: mouseYRelativeToBox / height
  };
};

/*
 Generates a CSS-type selector from an element
*/
export const getSelector = (target, selector = '') => {
  if (!target || !target.tagName) {
    return '' + selector;
  }

  const { tagName, id, className, parentElement } = target;

  // If it's the top level element (html) return the chained selector
  if (tagName === 'HTML') {
    return 'html' + selector;
  }

  let parsedClassName = '';

  if (typeof className === 'string') {
    parsedClassName = className
      .trim()
      .replace(/([.#>])/g, '\\\\$1')
      .split(/[\s\n]+/)
      .join('.');
  }

  let lowerCaseTagName = tagName.toLowerCase();

  if (id) {
    lowerCaseTagName = `${lowerCaseTagName}#${id}`;
  }

  if (parsedClassName) {
    lowerCaseTagName = `${lowerCaseTagName}.${parsedClassName}`;
  }

  // Recursive calls until the top element is reached (html)
  return getSelector(parentElement, `>${lowerCaseTagName}${selector}`);
};

/*
  Generates data that is stored as 'coordinates' in the db
*/
export const prepareTagInfo = (
  target: HTMLElement,
  mouseX: number,
  mouseY: number
): TagInfoType => {
  const mouseOffset = mouseOffsetElementInPercentage(target, mouseX, mouseY);
  return {
    path: getSelector(target),
    html: target.outerHTML.substring(0, 500), // I have no idea why this is "500"
    version: 2,
    data: {
      bugOffsetX: mouseOffset.x,
      bugOffsetY: mouseOffset.y
    }
  };
};

/*
  Generates coordinates to place the pin on a screenshot
*/
export const generateCoordinates = (
  target: HTMLElement,
  bugOffsetX: number,
  bugOffsetY: number
) => {
  const isProxyBugherd = window.location.hostname.includes('proxy.bugherd');
  const topWindow = isProxyBugherd ? window : window.top;
  const topDocument = topWindow?.document.documentElement;

  const { top, left, width, height } = getBoundingClientRectWithoutPadding(
    target
  );

  return generatePinCoordinates({
    pageOffset: {
      top: topDocument.offsetTop,
      left: topDocument.offsetLeft
    },
    windowWidth: topWindow.innerWidth,
    windowHeight: topWindow.innerHeight,
    scrollX: topWindow.scrollX,
    scrollY: topWindow.scrollY,
    documentWidth: topWindow.innerWidth,
    documentHeight: topWindow.innerHeight,
    bodyWidth: topWindow.document.body.offsetWidth,
    bodyHeight: topWindow.document.body.offsetHeight,
    visualViewport: visualViewportPolyfill(),
    target: {
      flagRelativeX: bugOffsetX * width,
      flagRelativeY: bugOffsetY * height,
      width: width,
      height: height,
      offset: {
        top:
          top +
          (window.document.documentElement.scrollTop ||
            window.document.body.scrollTop),
        left:
          left +
          (window.document.documentElement.scrollLeft ||
            window.document.body.scrollLeft)
      }
    }
  });
};
